import { ReactNode, useMemo } from 'react';
import { getCumulativeLtcPayoutSeries } from '@shared/calculations/funding/projection/projectionTotals';
import { TooltipLabel } from '../TooltipLabel';
import { ClaimantBadge } from './ClaimantBadge';
import { monthRowKey } from './formatters';
import { AggRenderContext, ColumnGroup, ColumnSpec, MonthRenderContext } from './types';

interface ProjectionTableProps {
  title?: string;
  subtitle?: ReactNode;
  groups: ColumnGroup[];
  months: MonthlyEntry[];
  // Cumulative LTC payout BEFORE the first month in this view, so the
  // running "paid to date" badge accounts for any payouts that happened
  // earlier in the projection (e.g. the EP lump row that triggered the
  // pre→payout split).
  cumulativeLtcPayoutBeforeFirstMonth?: number;
  // When true, a cell whose `blankKey` matches the row above is faded, so
  // runs of unchanged values read as one block (the first row always shows).
  dimUnchanged?: boolean;
  // End-of-PROJECTION overrides for the Total row's aggregate context. When
  // `months` is a filtered slice (e.g. a joint policy viewed for one
  // claimant), the last visible row can sit mid-projection — these let the
  // caller pin the footer's closing balances and paid-to-date to the true
  // end of the unfiltered projection.
  closeStateAtEnd?: MonthlyState;
  cumulativeLtcPayoutAtEnd?: number;
  footer?: ReactNode;
}

export function ProjectionTable({
  title,
  subtitle,
  groups,
  months,
  cumulativeLtcPayoutBeforeFirstMonth = 0,
  dimUnchanged = false,
  closeStateAtEnd,
  cumulativeLtcPayoutAtEnd,
  footer,
}: ProjectionTableProps) {
  const flatColumns = useMemo(() => groups.flatMap(g => g.columns), [groups]);

  // Group "bands" for the top header row. Adjacent groups sharing the same
  // `title` collapse into one band (Payout and Cost both live under "This
  // month", and CM II's strategy-extra surplus group joins "Running balances").
  // Tracks the index of the first column in each band so body rows can draw a
  // strong left border on band-boundary cells.
  const bands = useMemo(() => {
    const out: { title: string; colSpan: number; startIndex: number }[] = [];
    let col = 0;
    for (const g of groups) {
      const last = out[out.length - 1];
      const title = g.title ?? '';
      if (last && last.title === title) {
        last.colSpan += g.columns.length;
      } else {
        out.push({ title, colSpan: g.columns.length, startIndex: col });
      }
      col += g.columns.length;
    }
    return out;
  }, [groups]);

  const bandStartIndexes = useMemo(
    () => new Set(bands.slice(1).map(b => b.startIndex)),
    [bands],
  );
  const isBandStart = (ci: number) => bandStartIndexes.has(ci);

  // Running cumulative LTC payout per month — offset by anything paid before
  // this view started. `series[i]` is "paid through month i, inclusive".
  const cumulativeSeries = useMemo<number[]>(() => {
    const local = getCumulativeLtcPayoutSeries(months);
    return local.map(v => v + cumulativeLtcPayoutBeforeFirstMonth);
  }, [months, cumulativeLtcPayoutBeforeFirstMonth]);

  // Index of the previous row belonging to the SAME claimant. Joint streams
  // interleave the spouses, so `months[i - 1]` is often the other spouse — the
  // wrong baseline for the "dim unchanged" comparison (and its cumulative
  // payout). Single-life rows have no claimant, so this is just `i - 1`.
  const prevSameClaimantIndex = useMemo<(number | null)[]>(() => {
    const lastIndexByClaimant = new Map<string, number>();
    return months.map((month, i) => {
      const claimantKey = month.context.claimant ?? '__single__';
      const prev = lastIndexByClaimant.get(claimantKey) ?? null;
      lastIndexByClaimant.set(claimantKey, i);
      return prev;
    });
  }, [months]);

  const showHeaderBar = Boolean(title);

  return (
    <div>
      {showHeaderBar && (
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2">
          <div>
            {title && <span className="font-medium text-gray-900">{title}</span>}
            {subtitle && <span className="ml-2 text-sm text-gray-500">{subtitle}</span>}
          </div>
        </div>
      )}
      <div className="max-h-[70vh] overflow-auto">
        <table className="min-w-full border-collapse text-xs">
          <thead className="sticky top-0 z-20 bg-white shadow-[0_1px_0_rgba(0,0,0,0.08)]">
            <tr>
              {bands.map((band, bi) => (
                <th
                  key={`band-${bi}`}
                  colSpan={band.colSpan}
                  className={`bg-gray-50 px-3 pb-1.5 pt-2 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-gray-500 ${
                    bi > 0 ? 'border-l-2 border-l-gray-300' : ''
                  }`}
                >
                  {band.title}
                </th>
              ))}
            </tr>
            <tr>
              {flatColumns.map((col, ci) => {
                const stickyClasses = col.sticky
                  ? 'sticky left-0 z-10 bg-white shadow-[1px_0_0_rgba(0,0,0,0.06)]'
                  : '';
                const bandClass = isBandStart(ci) ? 'border-l-2 border-l-gray-300' : '';
                return (
                  <th
                    key={col.key}
                    className={`${stickyClasses} ${bandClass} whitespace-nowrap border-r border-gray-200 bg-white px-3 pb-2 pt-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-700`}
                  >
                    <TooltipLabel
                      label={col.label}
                      labelSuffix={col.labelSuffix}
                      tooltip={col.tooltip}
                    />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {months.map((month, i) => {
              // Each row carries its true opening state; reading it directly
              // stays correct when `months` is a non-contiguous (joint /
              // view-filtered) slice, where `months[i - 1]` is not the row
              // that actually preceded this one in the projection.
              const openState = month.openState;
              const prevIndex = prevSameClaimantIndex[i];
              const prevMonth = prevIndex != null ? months[prevIndex] : null;
              return (
                <DetailRow
                  key={monthRowKey(month)}
                  columns={flatColumns}
                  month={month}
                  openState={openState}
                  cumulativeLtcPayoutToDate={cumulativeSeries[i]}
                  prevMonth={prevMonth}
                  prevCumulativeLtcPayoutToDate={
                    prevIndex != null ? cumulativeSeries[prevIndex] : 0
                  }
                  dimUnchanged={dimUnchanged}
                  isBandStart={isBandStart}
                />
              );
            })}
          </tbody>
          <tfoot>
            {months.length > 0 && (
              <TotalRow
                columns={flatColumns}
                months={months}
                closeStateAtEnd={closeStateAtEnd ?? months[months.length - 1].state}
                cumulativeLtcPayoutAtEnd={
                  cumulativeLtcPayoutAtEnd ?? cumulativeSeries[cumulativeSeries.length - 1]
                }
                isBandStart={isBandStart}
              />
            )}
            {footer}
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function DetailRow({
  columns,
  month,
  openState,
  cumulativeLtcPayoutToDate,
  prevMonth,
  prevCumulativeLtcPayoutToDate,
  dimUnchanged,
  isBandStart,
}: {
  columns: ColumnSpec[];
  month: MonthlyEntry;
  openState: MonthlyState;
  cumulativeLtcPayoutToDate: number;
  prevMonth: MonthlyEntry | null;
  prevCumulativeLtcPayoutToDate: number;
  dimUnchanged: boolean;
  isBandStart: (ci: number) => boolean;
}) {
  const ctx: MonthRenderContext = { month, openState, cumulativeLtcPayoutToDate };
  const prevCtx: MonthRenderContext | null = prevMonth
    ? {
        month: prevMonth,
        openState: prevMonth.openState,
        cumulativeLtcPayoutToDate: prevCumulativeLtcPayoutToDate,
      }
    : null;
  // A cell dims when "dim unchanged" is on, there is a row above, and the
  // column's comparable value matches it. `null` keys opt the row out. The
  // value stays rendered (and its tooltip/reasons readable) — just faded so
  // runs of identical values read as one block.
  const isDimmed = (col: ColumnSpec) => {
    if (!dimUnchanged || !prevCtx || !col.blankKey) return false;
    const key = col.blankKey(ctx);
    return key != null && key === col.blankKey(prevCtx);
  };
  return (
    <tr className="border-b border-gray-100 bg-white hover:bg-gray-50">
      {columns.map((col, ci) => {
        const stickyClasses = col.sticky
          ? 'sticky left-0 z-[1] bg-white shadow-[1px_0_0_rgba(0,0,0,0.06)]'
          : '';
        const bandClass = isBandStart(ci) ? 'border-l-2 border-l-gray-300' : '';
        const dimClass = isDimmed(col) ? 'opacity-30' : '';
        // Joint policies tag each row with a spouse; show a badge in the first
        // column so interleaved rows are distinguishable. Single-life rows have
        // no claimant and render exactly as before.
        const showClaimant = ci === 0 && month.context.claimant != null;
        return (
          <td
            key={col.key}
            className={`${stickyClasses} ${bandClass} ${dimClass} whitespace-nowrap border-r border-gray-100 px-2 py-1 align-middle text-gray-800`}
          >
            {showClaimant ? (
              <div className="flex items-center gap-1.5">
                <ClaimantBadge claimant={month.context.claimant} />
                {col.render(ctx)}
              </div>
            ) : (
              col.render(ctx)
            )}
          </td>
        );
      })}
    </tr>
  );
}

// Bottom "Total" row: sums (cost), averages (benefit cap), and closing
// balances (pool, death benefit, surplus) across every visible month. Each
// column owns its own summary via `renderAggregate`; columns without one render
// an em dash. `closeStateAtEnd` is the end-of-projection closing state, so on
// a joint policy (interleaved or claimant-filtered spouse rows) the balances
// read the true end-state regardless of which row is last visible.
function TotalRow({
  columns,
  months,
  closeStateAtEnd,
  cumulativeLtcPayoutAtEnd,
  isBandStart,
}: {
  columns: ColumnSpec[];
  months: MonthlyEntry[];
  closeStateAtEnd: MonthlyState;
  cumulativeLtcPayoutAtEnd: number;
  isBandStart: (ci: number) => boolean;
}) {
  const ctx: AggRenderContext = {
    months,
    closeStateAtEnd,
    cumulativeLtcPayoutAtEnd,
    label: 'Total',
  };
  return (
    <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold">
      {columns.map((col, ci) => {
        const stickyClasses = col.sticky
          ? 'sticky left-0 z-[1] bg-gray-50 shadow-[1px_0_0_rgba(0,0,0,0.06)]'
          : '';
        const bandClass = isBandStart(ci) ? 'border-l-2 border-l-gray-300' : '';
        const cellClass = `${stickyClasses} ${bandClass} whitespace-nowrap border-r border-gray-200 px-2 py-2 align-middle text-gray-900`;
        const content = col.renderAggregate ? (
          col.renderAggregate(ctx)
        ) : (
          <span className="text-gray-300">—</span>
        );
        // The first cell carries the row's "Total" label — a row header for
        // accessibility. `text-left font-semibold` pins the UA's default th
        // styling to what the td rendered, so nothing shifts visually.
        if (ci === 0) {
          return (
            <th
              key={col.key}
              scope="row"
              className={`${cellClass} text-left font-semibold`}
            >
              {content}
            </th>
          );
        }
        return (
          <td
            key={col.key}
            className={cellClass}
          >
            {content}
          </td>
        );
      })}
    </tr>
  );
}
